/**
 * Confluent Event Tracking Service
 * 
 * Tracks all data flows for:
 * - Privacy compliance (COPPA for kids)
 * - Cost monitoring (API usage)
 * - Analytics without compromising privacy
 * 
 * Uses Confluent Cloud REST API for event streaming
 */

export type EventCategory = 
  | 'voice'      // ElevenLabs TTS/STT
  | 'ai'         // Vertex AI / Gemini calls
  | 'challenge'  // Challenge attempts
  | 'user'       // User actions
  | 'system';    // System events

export type EventType = 
  // Voice events
  | 'voice.tts.request'
  | 'voice.tts.complete'
  | 'voice.stt.request'
  | 'voice.stt.complete'
  // AI events
  | 'ai.recommendation.request'
  | 'ai.recommendation.complete'
  | 'ai.hint.request'
  | 'ai.hint.complete'
  | 'ai.analysis.request'
  | 'ai.analysis.complete'
  // Challenge events
  | 'challenge.start'
  | 'challenge.submit'
  | 'challenge.grade'
  | 'challenge.complete'
  // User events
  | 'user.login'
  | 'user.action'
  | 'user.achievement';

interface BaseEvent {
  eventId: string;
  eventType: EventType;
  category: EventCategory;
  timestamp: string;
  sessionId: string;
  userId?: string;        // Anonymized for kids
  isChild: boolean;
}

interface VoiceEvent extends BaseEvent {
  category: 'voice';
  payload: {
    provider: 'elevenlabs' | 'browser';
    operation: 'tts' | 'stt';
    characterCount?: number;
    durationMs?: number;
    voiceId?: string;
    estimatedCost?: number;
  };
}

interface AIEvent extends BaseEvent {
  category: 'ai';
  payload: {
    provider: 'vertex' | 'gemini';
    model: string;
    operation: string;
    inputTokens?: number;
    outputTokens?: number;
    estimatedCost?: number;
    cached?: boolean;
  };
}

interface ChallengeEvent extends BaseEvent {
  category: 'challenge';
  payload: {
    challengeId: string;
    templateId: string;
    skillTag: string;
    action: string;
    score?: number;
    passed?: boolean;
    durationSec?: number;
  };
}

interface UserEvent extends BaseEvent {
  category: 'user';
  payload: {
    action: string;
    metadata?: Record<string, unknown>;
  };
}

interface SystemEvent extends BaseEvent {
  category: 'system';
  payload: {
    action: string;
    metadata?: Record<string, unknown>;
  };
}

export type TrackingEvent = VoiceEvent | AIEvent | ChallengeEvent | UserEvent | SystemEvent;

// Cost tracking thresholds
const COST_LIMITS = {
  daily: {
    voice: 5.00,      // $5/day for ElevenLabs
    ai: 10.00,        // $10/day for Vertex/Gemini
    total: 15.00,
  },
  perUser: {
    voice: 0.50,      // $0.50/user/day
    ai: 1.00,         // $1/user/day
  },
  alerts: {
    warning: 0.8,     // Alert at 80% of limit
    critical: 0.95,   // Hard limit at 95%
  },
};

// Estimated costs per operation
const COST_ESTIMATES = {
  elevenlabs: {
    ttsPerChar: 0.00003,  // ~$0.03 per 1000 chars
  },
  vertex: {
    inputPerToken: 0.00000125,   // Gemini Pro pricing
    outputPerToken: 0.0000025,
  },
  gemini: {
    inputPerToken: 0.00000035,   // Gemini Flash pricing
    outputPerToken: 0.0000007,
  },
};

class ConfluentTracker {
  private config: {
    bootstrapServer: string;
    apiKey: string;
    apiSecret: string;
    topic: string;
  } | null = null;

  private sessionId: string;
  private eventQueue: TrackingEvent[] = [];
  private flushInterval: number | null = null;
  private dailyCosts: Record<EventCategory, number> = {
    voice: 0,
    ai: 0,
    challenge: 0,
    user: 0,
    system: 0,
  };
  private userCosts: Map<string, Record<EventCategory, number>> = new Map();

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeConfig();
    this.startFlushInterval();
    this.loadDailyCosts();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  private initializeConfig() {
    const bootstrapServer = import.meta.env.VITE_CONFLUENT_BOOTSTRAP_SERVER;
    const apiKey = import.meta.env.VITE_CONFLUENT_API_KEY;
    const apiSecret = import.meta.env.VITE_CONFLUENT_API_SECRET;
    const topic = import.meta.env.VITE_CONFLUENT_TOPIC || 'goalblox-events';

    if (bootstrapServer && apiKey && apiSecret) {
      this.config = { bootstrapServer, apiKey, apiSecret, topic };
    }
  }

  private startFlushInterval() {
    // Flush events every 5 seconds
    this.flushInterval = window.setInterval(() => {
      this.flush();
    }, 5000);
  }

  private loadDailyCosts() {
    const today = new Date().toISOString().split('T')[0];
    const stored = localStorage.getItem(`goalblox_costs_${today}`);
    if (stored) {
      try {
        this.dailyCosts = JSON.parse(stored);
      } catch {
        // Ignore parse errors
      }
    }
  }

  private saveDailyCosts() {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(`goalblox_costs_${today}`, JSON.stringify(this.dailyCosts));
  }

  private createBaseEvent(type: EventType, category: EventCategory, userId?: string, isChild = false): BaseEvent {
    return {
      eventId: `evt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      eventType: type,
      category,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: userId ? this.anonymizeUserId(userId, isChild) : undefined,
      isChild,
    };
  }

  // COPPA-compliant anonymization for children
  private anonymizeUserId(userId: string, isChild: boolean): string {
    if (!isChild) return userId;
    
    // Hash the user ID for children to comply with COPPA
    const encoder = new TextEncoder();
    const data = encoder.encode(userId + 'goalblox_salt');
    
    // Simple hash (in production, use SubtleCrypto)
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data[i];
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return `child_${Math.abs(hash).toString(36)}`;
  }

  // Check if we're within cost limits
  checkCostLimits(category: EventCategory, estimatedCost: number, userId?: string): {
    allowed: boolean;
    reason?: string;
    remaining: number;
  } {
    const dailyLimit = COST_LIMITS.daily[category as keyof typeof COST_LIMITS.daily] || COST_LIMITS.daily.total;
    const currentDaily = this.dailyCosts[category] || 0;
    const warningThreshold = dailyLimit * COST_LIMITS.alerts.warning;
    const criticalThreshold = dailyLimit * COST_LIMITS.alerts.critical;

    // Check daily limits
    if (currentDaily + estimatedCost > criticalThreshold) {
      return {
        allowed: false,
        reason: `Daily ${category} cost limit reached ($${currentDaily.toFixed(2)}/$${dailyLimit.toFixed(2)})`,
        remaining: Math.max(0, criticalThreshold - currentDaily),
      };
    }

    // Check per-user limits if userId provided
    if (userId) {
      const userLimit = COST_LIMITS.perUser[category as keyof typeof COST_LIMITS.perUser];
      if (userLimit) {
        const userCosts = this.userCosts.get(userId) || { voice: 0, ai: 0, challenge: 0, user: 0, system: 0 };
        const userCurrentCost = userCosts[category] || 0;
        
        if (userCurrentCost + estimatedCost > userLimit * COST_LIMITS.alerts.critical) {
          return {
            allowed: false,
            reason: `User ${category} cost limit reached`,
            remaining: Math.max(0, userLimit - userCurrentCost),
          };
        }
      }
    }

    // Warn if approaching limit
    if (currentDaily + estimatedCost > warningThreshold) {
      console.warn(`[CostTracker] Approaching ${category} daily limit: $${currentDaily.toFixed(2)}/$${dailyLimit.toFixed(2)}`);
    }

    return {
      allowed: true,
      remaining: criticalThreshold - currentDaily - estimatedCost,
    };
  }

  // Track voice events (ElevenLabs)
  trackVoice(params: {
    operation: 'tts' | 'stt';
    provider?: 'elevenlabs' | 'browser';
    characterCount?: number;
    durationMs?: number;
    voiceId?: string;
    userId?: string;
    isChild?: boolean;
    success?: boolean;
  }) {
    const { operation, provider = 'elevenlabs', characterCount = 0, ...rest } = params;

    // Calculate estimated cost
    let estimatedCost = 0;
    if (provider === 'elevenlabs' && operation === 'tts' && characterCount > 0) {
      estimatedCost = characterCount * COST_ESTIMATES.elevenlabs.ttsPerChar;
    }

    // Update costs
    this.dailyCosts.voice += estimatedCost;
    this.saveDailyCosts();

    if (params.userId) {
      const userCosts = this.userCosts.get(params.userId) || { voice: 0, ai: 0, challenge: 0, user: 0, system: 0 };
      userCosts.voice += estimatedCost;
      this.userCosts.set(params.userId, userCosts);
    }

    const eventType: EventType = operation === 'tts' 
      ? (params.success !== false ? 'voice.tts.complete' : 'voice.tts.request')
      : (params.success !== false ? 'voice.stt.complete' : 'voice.stt.request');

    const event: VoiceEvent = {
      ...this.createBaseEvent(eventType, 'voice', params.userId, params.isChild),
      category: 'voice',
      payload: {
        provider,
        operation,
        characterCount,
        durationMs: rest.durationMs,
        voiceId: rest.voiceId,
        estimatedCost,
      },
    };

    this.eventQueue.push(event);
  }

  // Track AI events (Vertex/Gemini)
  trackAI(params: {
    provider: 'vertex' | 'gemini';
    model: string;
    operation: string;
    inputTokens?: number;
    outputTokens?: number;
    cached?: boolean;
    userId?: string;
    isChild?: boolean;
    success?: boolean;
  }) {
    const { provider, model, operation, inputTokens = 0, outputTokens = 0, cached = false } = params;

    // Calculate estimated cost
    let estimatedCost = 0;
    if (!cached) {
      const pricing = COST_ESTIMATES[provider];
      estimatedCost = (inputTokens * pricing.inputPerToken) + (outputTokens * pricing.outputPerToken);
    }

    // Update costs
    this.dailyCosts.ai += estimatedCost;
    this.saveDailyCosts();

    if (params.userId) {
      const userCosts = this.userCosts.get(params.userId) || { voice: 0, ai: 0, challenge: 0, user: 0, system: 0 };
      userCosts.ai += estimatedCost;
      this.userCosts.set(params.userId, userCosts);
    }

    const eventType: EventType = params.success !== false 
      ? `ai.${operation}.complete` as EventType
      : `ai.${operation}.request` as EventType;

    const event: AIEvent = {
      ...this.createBaseEvent(eventType, 'ai', params.userId, params.isChild),
      category: 'ai',
      payload: {
        provider,
        model,
        operation,
        inputTokens,
        outputTokens,
        estimatedCost,
        cached,
      },
    };

    this.eventQueue.push(event);
  }

  // Track challenge events
  trackChallenge(params: {
    challengeId: string;
    templateId: string;
    skillTag: string;
    action: 'start' | 'submit' | 'grade' | 'complete';
    score?: number;
    passed?: boolean;
    durationSec?: number;
    userId?: string;
    isChild?: boolean;
  }) {
    const event: ChallengeEvent = {
      ...this.createBaseEvent(`challenge.${params.action}` as EventType, 'challenge', params.userId, params.isChild),
      category: 'challenge',
      payload: {
        challengeId: params.challengeId,
        templateId: params.templateId,
        skillTag: params.skillTag,
        action: params.action,
        score: params.score,
        passed: params.passed,
        durationSec: params.durationSec,
      },
    };

    this.eventQueue.push(event);
  }

  // Track user actions
  trackUser(params: {
    action: string;
    metadata?: Record<string, unknown>;
    userId?: string;
    isChild?: boolean;
  }) {
    const event: UserEvent = {
      ...this.createBaseEvent('user.action', 'user', params.userId, params.isChild),
      category: 'user',
      payload: {
        action: params.action,
        metadata: params.metadata,
      },
    };

    this.eventQueue.push(event);
  }

  // Get current cost summary
  getCostSummary(): {
    daily: Record<EventCategory, number>;
    limits: typeof COST_LIMITS.daily;
    percentUsed: Record<string, number>;
  } {
    const percentUsed: Record<string, number> = {};
    
    for (const [category, cost] of Object.entries(this.dailyCosts)) {
      const limit = COST_LIMITS.daily[category as keyof typeof COST_LIMITS.daily] || COST_LIMITS.daily.total;
      percentUsed[category] = (cost / limit) * 100;
    }

    return {
      daily: this.dailyCosts,
      limits: COST_LIMITS.daily,
      percentUsed,
    };
  }

  // Flush events to Confluent
  async flush() {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    if (!this.config) {
      // Log locally if Confluent not configured
      console.debug('[ConfluentTracker] Events (local):', events);
      return;
    }

    try {
      // Confluent REST Proxy format
      const records = events.map(event => ({
        key: { type: 'string', data: event.eventId },
        value: { type: 'json', data: event },
      }));

      const response = await fetch(
        `${this.config.bootstrapServer}/topics/${this.config.topic}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.kafka.json.v2+json',
            'Authorization': `Basic ${btoa(`${this.config.apiKey}:${this.config.apiSecret}`)}`,
          },
          body: JSON.stringify({ records }),
        }
      );

      if (!response.ok) {
        console.error('[ConfluentTracker] Failed to send events:', response.status);
        // Re-queue failed events
        this.eventQueue.push(...events);
      }
    } catch (err) {
      console.error('[ConfluentTracker] Error:', err);
      // Re-queue on error
      this.eventQueue.push(...events);
    }
  }

  // Cleanup
  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flush();
  }
}

// Singleton instance
export const tracker = new ConfluentTracker();

// React hook for easy access
export function useTracker() {
  return tracker;
}
